import { useEffect, useRef, useState, type FormEvent } from 'react'
import { apiFetch } from '../../lib/api'
import type { HeldBookItem, SavedHeldDismissal } from './bookWorkApi'

interface Props {
  item: HeldBookItem
  fundId: string
  onPendingChange: (pending: boolean) => void
  onSaved: () => void
}

type Receipt = Omit<SavedHeldDismissal, 'earlier_review'> & { command_id: string }

export default function HeldDismissalAction({ item, fundId, onPendingChange, onSaved }: Props) {
  const [reason, setReason] = useState('')
  const [pending, setPending] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState('')
  const active = useRef(false)
  const inFlight = useRef(false)
  const attempt = useRef<{ input: string; id: string } | null>(null)
  useEffect(() => {
    active.current = true
    return () => {
      active.current = false
    }
  }, [])

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (inFlight.current || saved || !reason.trim()) return
    const input = JSON.stringify({
      revision: item.revision,
      scope: { fund_id: fundId },
      reason: reason.trim(),
    })
    if (attempt.current?.input !== input) attempt.current = { input, id: crypto.randomUUID() }
    const command = attempt.current
    inFlight.current = true
    setPending(true)
    setError('')
    onPendingChange(true)
    try {
      const result = await apiFetch<Receipt>(
        `/api/rk/decks/group/${encodeURIComponent(item.group_id)}/held/${encodeURIComponent(item.item_id)}/dismissal`,
        {
          method: 'POST',
          body: JSON.stringify({ ...JSON.parse(command.input), command_id: command.id }),
        }
      )
      if (
        result.command_id !== command.id ||
        result.item_id !== item.item_id ||
        result.revision !== item.revision ||
        result.reason !== reason.trim() ||
        typeof result.dismissal_id !== 'string' ||
        !result.dismissal_id ||
        typeof result.actor_id !== 'string' ||
        !result.actor_id ||
        result.engine_hold_unchanged !== true ||
        result.financial_authority !== false ||
        result.evidence_waived !== false
      )
        throw new Error('The dismissal could not be confirmed. Refresh before continuing.')
      if (active.current) {
        setSaved(true)
        onSaved()
      }
    } catch (failure) {
      if (active.current)
        setError(failure instanceof Error ? failure.message : 'Could not save the dismissal.')
    } finally {
      inFlight.current = false
      if (active.current) {
        setPending(false)
        onPendingChange(false)
      }
    }
  }

  if (saved) return <p role="status">Marked not relevant. Financial checks still apply.</p>
  return (
    <form onSubmit={submit} aria-label="Dismiss as not relevant">
      <label>
        Reason for dismissal
        <textarea
          value={reason}
          maxLength={2000}
          required
          disabled={pending}
          onChange={(event) => setReason(event.target.value)}
        />
      </label>
      <p>This records your reason. Financial checks still apply.</p>
      <button type="submit" disabled={pending || !reason.trim()}>
        {pending ? 'Saving dismissal…' : 'Dismiss as not relevant'}
      </button>
      {error ? <p role="alert">{error}</p> : null}
    </form>
  )
}
